import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, CardBody, Col, Container, Row,
  Form, FormGroup, Label, Input, Button, CardHeader, Spinner
} from 'reactstrap';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory, useParams } from "react-router-dom";
import Cropper from 'react-easy-crop';

import { getDLDealCroppedImg } from '../../Components/Common/DealCropDL';

function UpdateDSDeal() {
  const history = useHistory();
  const { dealId } = useParams(); 

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    categoryId: '',
    days: [],
    dealPhoto: null,
    removePhoto: false,
    currentPhotoFile: null
  });
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [dealLoading, setDealLoading] = useState(true);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');
  const [dealPhotoPreview, setDealPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Cropper states (using react-easy-crop like the first page)
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  const daysOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    return () => {
      if (dealPhotoPreview) {
        URL.revokeObjectURL(dealPhotoPreview);
      }
    };
  }, [dealPhotoPreview]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}categories`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setCategories(data.responseData?.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error(`Failed to load categories: ${error.message}`);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchDeal = async () => {
      if (!dealId) {
        toast.error('Deal ID is missing.');
        return;
      }

      try {
        setDealLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}business/deal/dailySpecials/${dealId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const deal = data.responseData?.deal;

        if (deal) {
          // Set photo preview if exists
          if (deal.dealPhoto?.url) {
            setDealPhotoPreview(deal.dealPhoto.url);
          }
 let photoFile = null;
          if (deal.dealPhoto?.url) {
            try {
              const photoBlob = await fetch(deal.dealPhoto.url).then(r => r.blob());
              photoFile = new File([photoBlob], 'existing-deal-photo.jpg', { type: 'image/jpeg' });
            } catch (error) {
              console.error('Error converting photo URL to File:', error);
            }
          }
          setFormData({
            name: deal.name || '',
            price: deal.price || '',
            categoryId: deal.categoryId || '',
            days: deal.days || [],
            dealPhoto: null,
            removePhoto: false,
            currentPhotoFile: photoFile
          });
        } else {
          throw new Error('Deal data not found');
        }
      } catch (error) {
        console.error('Error fetching deal:', error);
        toast.error(`Failed to load deal: ${error.message}`);
      } finally {
        setDealLoading(false);
      }
    };

    fetchDeal();
  }, [dealId]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);
      const reader = new FileReader();

      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropper(true);
        e.target.value = '';
      };

      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async () => {
    try {
      const croppedImageBlob = await getDLDealCroppedImg(
        imageToCrop,
        croppedAreaPixels
      );

      const fileName = 'deal-photo.jpg';
      const croppedImageFile = new File([croppedImageBlob], fileName, {
        type: 'image/jpeg',
      });

      const previewUrl = URL.createObjectURL(croppedImageBlob);

      setFormData(prev => ({
        ...prev,
        dealPhoto: croppedImageFile,
        removePhoto: false,
        currentPhotoFile: null
      }));

      setDealPhotoPreview(previewUrl);
      setShowCropper(false);
    } catch (e) {
      toast.error('Error cropping image');
      console.error('Error cropping image', e);
    }
  };

  const handleRemovePhoto = () => {
    setFormData(prev => ({
      ...prev,
      dealPhoto: null,
      removePhoto: true,
      currentPhotoFile: null
    }));
    setDealPhotoPreview(null);
    setSelectedFileName('No file chosen');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDayChange = (day) => {
    setFormData(prev => {
      const days = [...prev.days];
      return {
        ...prev,
        days: days.includes(day)
          ? days.filter(d => d !== day)
          : [...days, day]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!dealId) {
      toast.error('Deal ID is missing.');
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('categoryId', formData.categoryId);
      
      // Handle photo scenarios
      if (formData.removePhoto) {
        formDataToSend.append('removePhoto', 'true');
      } else if (formData.dealPhoto) {
        // New photo uploaded
        formDataToSend.append('dealPhoto', formData.dealPhoto);
      } else if (formData.currentPhotoFile) {
        // Send existing photo file
        formDataToSend.append('dealPhoto', formData.currentPhotoFile);
      }
      // Note: We don't need to handle currentPhotoFile as we're not sending the existing file back
      
      formData.days.forEach(day => {
        formDataToSend.append('days[]', day);
      });

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}business/deal/dailySpecials/${dealId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Update response:', data);

      const isSuccess = data.responseCode === 200 ||
        (Array.isArray(data.responseMessage) &&
         data.responseMessage.some(msg =>
           msg.toLowerCase().includes('success') ||
           msg.toLowerCase().includes('successfully')
         ));

      if (isSuccess) {
        toast.success('Daily special updated successfully!');
        setTimeout(() => {
          history.push('/deal-list');
        }, 1500);
      } else {
        throw new Error(data.responseMessage?.join(', ') || 'Failed to update daily special');
      }
    } catch (error) {
      console.error('Error updating form:', error);
      toast.error(`Failed to update daily special: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Update Daily Special</title>
        </MetaTags>
        <ToastContainer />

        <div className="mb-3 ms-4"></div>

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className='d-flex align-items-center'>
                <Link to="/deal-list">
                  <Button color="primary" className="btn">
                    <i className="mdi mdi-arrow-left"></i>
                  </Button>
                </Link>
                <h4 className="card-title mb-0 ms-2">Update Daily Special</h4>
              </CardHeader>
              <CardBody>
                {dealLoading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    <span className="ms-2">Loading deal data...</span>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Name</Label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter special name"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Price</Label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <Input
                              type="text"
                              name="price"
                              value={formData.price}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*\.?\d*$/.test(value)) {
                                  handleInputChange(e);
                                }
                              }}
                              placeholder="Enter price"
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Category</Label>
                          <Input
                            type="select"
                            name="categoryId"
                            value={formData.categoryId}
                            onChange={handleInputChange}
                            disabled={categoriesLoading}
                          >
                            <option value="">Select a category</option>
                            {categories.map(category => (
                              <option key={category._id} value={category._id}>
                                {category.name}
                              </option>
                            ))}
                          </Input>
                          {categoriesLoading && <small className="text-muted">Loading categories...</small>}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Deal Photo</Label>
                          <div className="d-flex">
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => fileInputRef.current.click()}
                              style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0
                              }}
                            >
                              Choose File
                            </Button>
                            <Input
                              type="text"
                              readOnly
                              value={selectedFileName}
                              style={{
                                flex: 1,
                                cursor: "pointer",
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0
                              }}
                              onClick={() => fileInputRef.current.click()}
                            />
                            <input
                              type="file"
                              name="dealPhoto"
                              onChange={handleFileChange}
                              ref={fileInputRef}
                              accept="image/*"
                              style={{ display: "none" }}
                            />
                          </div>
                          {dealPhotoPreview && (
                            <div className="mt-2 d-flex align-items-center">
                              <img
                                src={dealPhotoPreview}
                                alt="Deal preview"
                                style={{
                                  maxWidth: '100px',
                                  maxHeight: '100px',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  marginRight: '10px'
                                }}
                              />
                              <Button
                                color="danger"
                                size="sm"
                                className="btn"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  backgroundColor: "transparent",
                                  color: "rgb(189, 9, 9)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  marginBottom: "25px",
                                  boxShadow: "none"
                                }}
                                onClick={handleRemovePhoto}
                              >
                                x
                              </Button>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <Label>Days</Label>
                          <div className="d-flex flex-wrap gap-2">
                            {daysOptions.map(day => (
                              <div key={day} className="form-check">
                                <Input
                                  type="checkbox"
                                  id={`day-${day}`}
                                  checked={formData.days.includes(day)}
                                  onChange={() => handleDayChange(day)}
                                />
                                <Label for={`day-${day}`} className="form-check-label">
                                  {day}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <Button type="submit" color="primary" disabled={loading || categoriesLoading || dealLoading}>
                        {loading ? 'Updating...' : 'Update Daily Special'}
                      </Button>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        
        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <h4>Crop your deal photo</h4>
              <div className="crop-container">
                  <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                 aspect={3 / 2}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  zoomWithWheel={true}
                  minZoom={0.5} 
                  maxZoom={3} 
                  wheelSpeed={0.01}
                />
              </div>
              <div className="crop-controls">
                <input
                  type="range"
                  value={zoom}
                  min={0.5}
                  max={3}
                  step={0.01}
                  onChange={(e) => setZoom(e.target.value)}
                />
                <div className="crop-buttons">
                  <Button color="secondary" onClick={() => setShowCropper(false)}>
                    Cancel
                  </Button>
                  <Button color="primary" onClick={handleCropComplete}>
                    Save Crop
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default UpdateDSDeal;